import * as React from 'react'

import { cn } from '#lib/utils'

interface OtpInputProps {
  otpLength?: number
  disabled?: boolean
  error?: boolean
  value: string
  onChange: (value: string) => void
  onComplete?: (value: string) => void
  onResendOtp?: () => Promise<void> | void
}

export const OtpInput = ({
  otpLength = 6,
  disabled = false,
  error = false,
  value,
  onChange,
  onComplete,
  onResendOtp,
}: OtpInputProps) => {
  const [otpArray, setOtpArray] = React.useState<string[]>(() => {
    const arr = Array.from({ length: otpLength }, () => '')
    if (value) {
      for (let i = 0; i < value.length && i < otpLength; i++) {
        arr[i] = value[i]
      }
    }
    return arr
  })

  const RESEND_DELAY = 120
  const [secondsLeft, setSecondsLeft] = React.useState<number>(RESEND_DELAY)
  const timerRef = React.useRef<NodeJS.Timeout | null>(null)
  const inputRefs = React.useRef<(HTMLInputElement | null)[]>([])

  React.useEffect(() => {
    const arr = Array.from({ length: otpLength }, () => '')
    if (value) {
      for (let i = 0; i < value.length && i < otpLength; i++) {
        arr[i] = value[i]
      }
    }
    setOtpArray(arr)
  }, [value, otpLength])

  React.useEffect(() => {
    startResendTimer()
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [])

  const startResendTimer = () => {
    setSecondsLeft(RESEND_DELAY)
    if (timerRef.current) {
      clearInterval(timerRef.current)
    }
    timerRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
          }
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleChange = (index: number, char: string) => {
    if (!/^\d?$/.test(char)) return

    const nextArr = [...otpArray]
    nextArr[index] = char
    const newValue = nextArr.join('')

    setOtpArray(nextArr)
    onChange(newValue)

    if (char && index < otpLength - 1) {
      inputRefs.current[index + 1]?.focus()
    }

    if (nextArr.every((c) => c !== '') && onComplete) {
      onComplete(newValue)
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpArray[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-between gap-2">
        {otpArray.map((char, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            disabled={disabled}
            value={char}
            onChange={(e) => {
              handleChange(index, e.target.value)
            }}
            onKeyDown={(e) => {
              handleKeyDown(index, e)
            }}
            className={cn(
              'h-12 w-10 rounded-lg border text-center text-xl font-bold outline-none transition-all',
              'focus:ring-2 focus:ring-offset-1',
              error
                ? 'border-red-300 bg-red-50 text-red-600 focus:ring-red-200'
                : 'border-gray-200 bg-white text-gray-900 focus:border-indigo-500 focus:ring-indigo-100',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          />
        ))}
      </div>
      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          disabled={disabled || secondsLeft > 0}
          onClick={async () => {
            if (onResendOtp) {
              await onResendOtp()
              startResendTimer()
              setOtpArray(Array(otpLength).fill(''))
            }
          }}
          className={cn(
            'font-medium transition-colors',
            secondsLeft > 0
              ? 'cursor-not-allowed text-slate-400'
              : 'text-indigo-600 hover:text-indigo-700',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          Renvoyer le code
          {secondsLeft > 0 && ` (${secondsLeft}s)`}
        </button>
      </div>
    </div>
  )
}
